import PropTypes from "prop-types"
import React, { useEffect, useState } from "react"
import MarinaInviteModal from "src/main/MarinaInvite/MarinaInviteModal"

import Button from "src/components/Button"
import TrackingLink from "src/components/TrackingLink"

import { useTracker } from "src/hooks/use_tracker"

const MarinaInvite = ({
  marina,
  loggedIn,
  user,
  minimumPasswordLength,
  shouldShowSuccessBanner,
  marinaHasPublicProfile,
}) => {
  const [showSuccessBanner, setShowSuccessBanner] = useState(
    shouldShowSuccessBanner
  )
  const tracker = useTracker()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [variant, setVariant] = useState(loggedIn ? "loggedIn" : "login")
  const trackingProps = {
    marina_id: marina.id,
    marina_mcom_id: marina.mcomId,
    marina_name: marina.name,
    user_id: user?.encodedId,
  }

  useEffect(function trackPageViewed() {
    tracker.trackEvent("marina_invite:page_viewed", trackingProps)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const renderSuccessBanner = () => (
    <div className="card col-span-12 p-8 lg:col-span-5 lg:row-span-1">
      <div className="flex flex-col items-center justify-center pb-6 text-center">
        <i className="fa-solid fa-circle-check text-5xl text-teal-400" />
        <h4 className="text-xl font-bold">You&apos;re Connected</h4>
        <span className="text-gray-600">
          We&apos;ll let {marina.name} you&apos;ve joined them on Dockwa. Now
          get out there and enjoy your time on the water!
        </span>
      </div>
    </div>
  )

  const renderButtons = () => {
    if (loggedIn) {
      return (
        <div>
          <Button
            onClick={() => {
              setIsModalOpen(true)
              setVariant("loggedIn")
              tracker.trackEvent(
                "marina_invite:connect_button_clicked",
                trackingProps
              )
            }}
            variant="primary"
          >
            Connect with {marina.name}
          </Button>
        </div>
      )
    } else if (window.workosAuthEnabled) {
      // AuthKit returns the boater to this invite through the return-to
      // location the invite page stored in the session.
      return (
        <div className="flex items-center space-x-4">
          <a className="btn btn-primary" href="/users/sign_in">
            Log in
          </a>
          <a className="btn btn-primary" href="/users/sign_up">
            Sign up
          </a>
        </div>
      )
    } else {
      return (
        <div className="flex items-center space-x-4">
          <Button
            onClick={() => {
              setIsModalOpen(true)
              setVariant("login")
            }}
            variant="primary"
          >
            Log in
          </Button>
          <Button
            onClick={() => {
              setIsModalOpen(true)
              setVariant("signup")
            }}
            variant="primary"
          >
            Sign up
          </Button>
        </div>
      )
    }
  }

  const renderContactInviteActions = () => (
    <>
      <div className="pb-6">
        <img
          src={marina.imageUrl}
          alt={marina.name}
          className="h-32 w-full rounded"
        />
      </div>
      <div className="flex flex-col items-center pb-6 lg:items-start">
        <h4 className="text-xl font-bold">Join {marina.name} on Dockwa</h4>
        <span className="text-gray-600">
          Connecting your Dockwa account with {marina.name} will allow you to
          view and manage any existing reservations, contracts, or invoices
          created for you by {marina.name}.
        </span>
        {loggedIn ? (
          <div className="mt-2 flex flex-col">
            <span>
              You&apos;re signed in as <strong>{user.name}</strong>
            </span>
            <span className="font-semibold">({user.email})</span>
          </div>
        ) : (
          <span className="mt-2 font-semibold">
            You need to log in or sign up first before connecting with{" "}
            {marina.name}
          </span>
        )}
        <div className="mt-6">{renderButtons()}</div>
      </div>
    </>
  )

  const renderOtherActions = () => (
    <>
      <h4 className="mt-0 text-lg font-semibold">
        Connecting with {marina.name} on Dockwa Unlocks
      </h4>
      <div className="flex flex-col space-y-6">
        <div className="flex space-x-4">
          {/* First row cards */}
          <div className="flex h-full w-1/2 flex-col justify-between">
            <div className="flex flex-col space-y-2">
              <div className="flex items-center space-x-2">
                <i className="fa-solid fa-calendar-days text-xl text-gray-900" />
                <span className="text-base">Past Booking History</span>
              </div>
              <span className="text-gray-600">
                View your active and upcoming reservations and contract details
                right from your account
              </span>
            </div>
            {showSuccessBanner && (
              <div className="mt-4">
                <TrackingLink
                  className="btn btn-tertiary"
                  href="/account/trips"
                  eventName="marina_invite:view_trips_pressed"
                  eventProperties={trackingProps}
                >
                  View bookings
                </TrackingLink>
              </div>
            )}
          </div>
          <div className="flex h-full w-1/2 flex-col justify-between">
            <div className="flex flex-col space-y-2">
              <div className="flex items-center space-x-2">
                <i className="fa-solid fa-comments text-xl text-gray-900" />
                <span className="text-base">Direct Messaging</span>
              </div>
              <span className="text-gray-600">
                Find, request, and pay for dockage, fuel, and more at thousands
                of marinas
              </span>
            </div>
            {showSuccessBanner && marinaHasPublicProfile && (
              <div className="mt-4">
                <TrackingLink
                  className="btn btn-tertiary"
                  href={`/explore/destination/${marina.mcomId}`}
                  eventName="marina_invite:view_marina"
                  eventProperties={trackingProps}
                >
                  View marina
                </TrackingLink>
              </div>
            )}
          </div>
        </div>

        <div className="flex space-x-4">
          {/* Second row cards */}
          <div className="flex h-full w-1/2 flex-col justify-between">
            <div className="flex flex-col space-y-2">
              <div className="flex items-center space-x-2">
                <i className="fa-solid fa-file-contract text-lg text-gray-900" />
                <span className="text-base">Document Sharing</span>
              </div>
              <span className="text-gray-600">
                Add additional boat info such as insurance and registration to
                profile to easily secure future dockage
              </span>
            </div>
            {showSuccessBanner && (
              <div className="mt-4">
                <TrackingLink
                  className="btn btn-tertiary"
                  href="/account/boats"
                  eventName="marina_invite:update_boater_info_pressed"
                  eventProperties={trackingProps}
                >
                  Update vessel info
                </TrackingLink>
              </div>
            )}
          </div>
          <div className="flex h-full w-1/2 flex-col justify-between">
            <div className="flex flex-col space-y-2">
              <div className="flex items-center space-x-2">
                <i className="fa-solid fa-credit-card text-base text-gray-900" />
                <span className="text-base">Streamlined Payments</span>
              </div>
              <span className="text-gray-600">
                Securely store credit card and ACH Direct Debit details for
                easy, automatic marina payments
              </span>
            </div>
            {showSuccessBanner && (
              <div className="mt-4">
                <TrackingLink
                  className="btn btn-tertiary"
                  href="/account/settings/payment_methods"
                  eventName="marina_invite:update_payment_info_pressed"
                  eventProperties={trackingProps}
                >
                  Update payment info
                </TrackingLink>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  )

  return (
    <div className="grid w-full grid-cols-12 gap-6 py-8 lg:grid-rows-2 lg:gap-13">
      <div className="col-span-12 lg:col-span-5">
        {showSuccessBanner
          ? renderSuccessBanner()
          : renderContactInviteActions()}
      </div>
      <div className="col-span-12 flex h-full items-center justify-center lg:col-span-1">
        <div className="hidden h-1/2 w-0 border-r border-gray-300 lg:block" />
        <div className="h-0 w-full border-b border-gray-300 lg:hidden" />
      </div>
      <div className="col-span-12 flex flex-col lg:col-span-5 lg:row-span-2">
        <div className="flex w-full flex-col">{renderOtherActions()}</div>
      </div>
      <MarinaInviteModal
        marina={marina}
        variant={variant}
        minimumPasswordLength={minimumPasswordLength}
        isOpen={isModalOpen}
        setIsOpen={setIsModalOpen}
        setShowSuccessBanner={setShowSuccessBanner}
        setVariant={setVariant}
        trackingProps={trackingProps}
      />
    </div>
  )
}

MarinaInvite.propTypes = {
  marina: PropTypes.shape({
    name: PropTypes.string.isRequired,
    id: PropTypes.number.isRequired,
    mcomId: PropTypes.string.isRequired,
    imageUrl: PropTypes.string.isRequired,
    terms: PropTypes.string,
  }).isRequired,
  loggedIn: PropTypes.bool.isRequired,
  user: PropTypes.shape({
    encodedId: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
  }),
  minimumPasswordLength: PropTypes.number.isRequired,
  shouldShowSuccessBanner: PropTypes.bool.isRequired,
  marinaHasPublicProfile: PropTypes.bool.isRequired,
}

export default MarinaInvite
